/**
 * Multi-factor authentication (TOTP) helpers backed by Lovable Cloud.
 *
 * TOTP works with any authenticator app — Google Authenticator, Microsoft
 * Authenticator, Authy, 1Password, etc. — so a single TOTP factor covers all
 * of them. SMS-based MFA would need a separate paid SMS provider configured.
 */
import { supabase } from "@/integrations/supabase/client";

export interface TotpFactor {
  id: string;
  friendly_name?: string;
  status: "verified" | "unverified";
}

export type AssuranceLevel = "aal1" | "aal2";

/** List the user's TOTP factors. */
export async function listTotpFactors(): Promise<TotpFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  return (data.totp ?? []) as TotpFactor[];
}

/** Returns { currentLevel, nextLevel } for the current session. */
export async function getAal(): Promise<{
  currentLevel: AssuranceLevel | null;
  nextLevel: AssuranceLevel | null;
}> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw new Error(error.message);
  return {
    currentLevel: (data.currentLevel ?? null) as AssuranceLevel | null,
    nextLevel: (data.nextLevel ?? null) as AssuranceLevel | null,
  };
}

export async function hasVerifiedTotp(): Promise<boolean> {
  const factors = await listTotpFactors();
  return factors.some((f) => f.status === "verified");
}

/**
 * Remove any leftover unverified factors so a fresh enrolment doesn't collide
 * with an abandoned one.
 */
export async function clearUnverifiedFactors(): Promise<void> {
  const factors = await listTotpFactors();
  await Promise.all(
    factors
      .filter((f) => f.status === "unverified")
      .map((f) => supabase.auth.mfa.unenroll({ factorId: f.id })),
  );
}

export interface TotpEnrollment {
  factorId: string;
  qrCode: string; // SVG data URL — render directly in an <img>
  secret: string; // manual-entry key
  uri: string;
}

/** Start a new TOTP enrolment. Returns the QR code + secret to display. */
export async function enrollTotp(friendlyName?: string): Promise<TotpEnrollment> {
  await clearUnverifiedFactors();
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    // Friendly names must be unique per user.
    friendlyName: friendlyName ?? `Authenticator ${new Date().toLocaleDateString()} ${Date.now().toString().slice(-4)}`,
  });
  if (error) throw new Error(error.message);
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

/** Verify a 6-digit code against a factor. Elevates the session to aal2. */
export async function verifyTotp(factorId: string, code: string): Promise<void> {
  const challenge = await supabase.auth.mfa.challenge({ factorId });
  if (challenge.error) throw new Error(challenge.error.message);
  const verify = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.data.id,
    code: code.replace(/\s/g, ""),
  });
  if (verify.error) throw new Error(verify.error.message);
}

export async function unenrollTotp(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}
